import { IMAGES } from "./images";

// Every topic renders through TopicPage. Timeline and Òrìṣà Encyclopedia have dedicated pages.
export const TOPICS = {
  // ============ LEARNING HUB — WHO ARE THE YORÙBÁ ============
  "origins-and-history": {
    slug: "origins-and-history",
    title: "Origins & History of the Yorùbá",
    category: "learning",
    categoryLabel: "Who Are the Yorùbá?",
    hero: IMAGES.storytellingPortrait,
    metaDescription:
      "Discover the origins and history of the Yorùbá people, from the sacred city of Ilé-Ifẹ̀ and the myth of Odùduwà to the great empire of Ọ̀yọ́.",
    keywords: ["Yorùbá origins", "Ilé-Ifẹ̀", "Odùduwà", "Ọ̀yọ́ empire", "Yorùbá history"],
    intro:
      "The Yorùbá are one of Africa's largest and most culturally influential peoples, numbering more than 45 million across Nigeria, Benin, Togo, and a vast global diaspora. Their story begins in Ilé-Ifẹ̀, the sacred city the Yorùbá regard as the cradle of creation and the birthplace of humanity itself.",
    sections: [
      {
        heading: "The Cradle of Creation: Ilé-Ifẹ̀",
        body: [
          "According to Yorùbá tradition, the world was created at Ilé-Ifẹ̀. The deity Odùduwà is remembered as the founding ancestor who descended to establish order and kingship, and from whom the ruling lineages of the Yorùbá city-states trace their legitimacy.",
          "Archaeology confirms Ifẹ̀ as an ancient urban centre. Between roughly the 11th and 15th centuries, its artists produced the extraordinary naturalistic bronze and terracotta heads that astonished the world when they were first documented — evidence of a sophisticated, centralised society.",
        ],
      },
      {
        heading: "City-States and the Rise of Ọ̀yọ́",
        body: [
          "The Yorùbá organised themselves into a network of independent kingdoms and city-states, each led by an ọba (king) but united by a shared language, religion, and sense of common descent from Odùduwà.",
          "From the 17th to the early 19th century, the Ọ̀yọ́ Empire became the dominant Yorùbá power, commanding a formidable cavalry and controlling trade routes across the savannah. Its political system balanced the power of the Aláàfin (king) with the Ọ̀yọ́ Mèsì council, an early expression of checks and balances.",
        ],
      },
      {
        heading: "Upheaval, Diaspora, and Endurance",
        body: [
          "The collapse of Ọ̀yọ́ in the early 1800s triggered decades of internal wars. Tragically, this period also fed the transatlantic slave trade, carrying hundreds of thousands of Yorùbá people to the Americas, where their culture took deep root in Brazil, Cuba, Trinidad, and beyond.",
          "Despite conquest, colonialism, and displacement, Yorùbá identity endured. Today the Yorùbá remain a living civilisation — proud of an unbroken thread that connects modern communities to Ilé-Ifẹ̀ and the dawn of their world.",
        ],
      },
    ],
    related: ["language", "kingship", "history-timeline", "global-influence-diaspora"],
  },

  language: {
    slug: "language",
    title: "The Yorùbá Language",
    category: "learning",
    categoryLabel: "Who Are the Yorùbá?",
    hero: "https://static.prod-images.emergentagent.com/jobs/505cc825-2461-4ba6-922d-8b16e3d69521/images/ee68f99748208dcd4678176ea7b3cf927e841b6d5a624d0aa31337a5c7fdbc0c.jpeg",
    metaDescription:
      "Explore the Yorùbá language — a musical, tonal tongue spoken by tens of millions, rich in proverbs, praise poetry, and deep cultural meaning.",
    keywords: ["Yorùbá language", "tonal language", "Yorùbá proverbs", "oríkì"],
    intro:
      "Yorùbá is a tonal language of the Niger-Congo family, spoken by tens of millions of people. More than a means of communication, it is a vessel of philosophy, memory, and identity — a language in which meaning rises and falls with the music of the voice.",
    sections: [
      {
        heading: "A Language of Tone and Music",
        body: [
          "Yorùbá uses three tones — high, mid, and low — and a single word can shift meaning entirely depending on its pitch. This musicality is why the talking drum can 'speak' Yorùbá, reproducing the tones of speech through rhythm.",
          "The written language uses subscript dots and tonal marks to capture these sounds precisely, giving Yorùbá text its distinctive and beautiful appearance.",
        ],
      },
      {
        heading: "Proverbs and the Weight of Words",
        body: [
          "The Yorùbá say, 'Òwe lẹṣin ọ̀rọ̀' — proverbs are the horses of speech; when speech is lost, proverbs are used to find it. Wisdom is transmitted through thousands of proverbs (òwe) that compress deep observation into memorable phrases.",
          "Oríkì, or praise poetry, is another treasured form — chanted recitations that celebrate the character, lineage, and deeds of people, deities, and towns.",
        ],
      },
      {
        heading: "A Global Language",
        body: [
          "Yorùbá vocabulary and ritual language survive in the Americas within Candomblé, Santería (Lucumí), and other traditions. Today Yorùbá is taught in universities worldwide and is experiencing a proud revival among diaspora communities reconnecting with their roots.",
        ],
      },
    ],
    related: ["arts-culture-creativity", "naming-system", "origins-and-history"],
  },

  "religion-and-spirituality": {
    slug: "religion-and-spirituality",
    title: "Religion & Spirituality",
    category: "learning",
    categoryLabel: "Who Are the Yorùbá?",
    hero: IMAGES.woodCarving,
    metaDescription:
      "An introduction to Yorùbá religion and African spirituality: Olódùmarè the Supreme Being, the Òrìṣà, ancestors, and the sacred wisdom of Ifá.",
    keywords: ["Yorùbá religion", "African spirituality", "Òrìṣà beliefs", "Olódùmarè", "Ifá"],
    intro:
      "Yorùbá spirituality (Ìṣẹ̀ṣe) is one of the world's great religious traditions. It envisions a universe animated by Àṣẹ — divine energy — and governed by a Supreme Being who works through hundreds of Òrìṣà, alongside the abiding presence of the ancestors.",
    sections: [
      {
        heading: "Olódùmarè and the Òrìṣà",
        body: [
          "At the summit stands Olódùmarè (also called Olórun), the Supreme Being and source of all Àṣẹ. Rather than being approached directly, Olódùmarè is served through the Òrìṣà — divine beings who each govern aspects of nature and human life.",
          "Ṣàngó rules thunder and justice; Ọ̀ṣun presides over rivers, love, and fertility; Ògún is the force of iron and labour; Ọbàtálá shapes human bodies. Each Òrìṣà has its own colours, foods, drum rhythms, and moral character.",
        ],
      },
      {
        heading: "Ifá: The Sacred Knowledge System",
        body: [
          "Ifá is the vast body of wisdom and divination associated with the Òrìṣà Ọ̀rúnmìlà, the deity of destiny and knowledge. Trained priests called babaláwo interpret sacred verses (odù) to offer guidance rooted in centuries of accumulated insight.",
          "In 2005, UNESCO recognised the Ifá divination system as a Masterpiece of the Oral and Intangible Heritage of Humanity.",
        ],
      },
      {
        heading: "Ancestors and the Living World",
        body: [
          "The ancestors (egúngún) remain active members of the community, honoured and consulted for guidance. For the Yorùbá, the spiritual and physical worlds are not separate realms but a single, interwoven reality.",
        ],
      },
    ],
    related: ["orisa-encyclopedia", "ifa-philosophy", "mythology", "death-and-ancestors"],
  },

  "social-structure": {
    slug: "social-structure",
    title: "Social Structure & Values",
    category: "learning",
    categoryLabel: "Who Are the Yorùbá?",
    hero: "https://static.prod-images.emergentagent.com/jobs/505cc825-2461-4ba6-922d-8b16e3d69521/images/c5a4bc114ede18c931b7fe56fca6cd8ac879177c36dcc143e6a4b18f6ccaa646.jpeg",
    metaDescription:
      "Understand Yorùbá social structure and values — extended family, lineage, respect for elders, community, and the ideal of Omolúàbí.",
    keywords: ["Yorùbá social structure", "Yorùbá values", "Omolúàbí", "respect for elders"],
    intro:
      "Yorùbá society is built on family, lineage, and a profound respect for elders and community. Individual identity is inseparable from one's place within a web of relationships stretching back to the ancestors and forward to unborn generations.",
    sections: [
      {
        heading: "Family and Lineage",
        body: [
          "The extended family (ẹbí) is the foundation of Yorùbá life. Members of a lineage share a common ancestor, praise poetry, and often a compound. This network provides identity, support, and moral accountability.",
          "Age and seniority structure daily interactions. Greetings — including prostrating (dòbálẹ̀) or kneeling (kúnlẹ̀) before elders — express a deep culture of respect (ìbọ̀wọ̀).",
        ],
      },
      {
        heading: "The Ideal of Omolúàbí",
        body: [
          "The highest social ideal is to be an Omolúàbí — a person of good character (ìwà rere). This encompasses honesty, humility, hard work, respect, courage, and service to the community. Character, not wealth, is the true measure of a person.",
        ],
      },
      {
        heading: "Community and Cooperation",
        body: [
          "Communal institutions such as the àáró and ọ̀wẹ̀ (cooperative labour groups) and age-grade associations bind people together. The Yorùbá proverb 'Ẹnìkan kì í dá gbé ayé' — one person cannot live the world alone — captures this ethic of interdependence.",
        ],
      },
    ],
    related: ["omoluabi", "ethics-moral-system", "naming-system", "life-stages"],
  },

  "arts-culture-creativity": {
    slug: "arts-culture-creativity",
    title: "Arts, Culture & Creativity",
    category: "learning",
    categoryLabel: "Who Are the Yorùbá?",
    hero: IMAGES.masks,
    metaDescription:
      "Explore Yorùbá arts and creativity — bronze casting, wood carving, adire textiles, beadwork, drumming, and a world-renowned artistic heritage.",
    keywords: ["Yorùbá art", "adire", "Yorùbá sculpture", "Ifẹ̀ bronzes", "Yorùbá culture"],
    intro:
      "The Yorùbá possess one of Africa's richest artistic traditions, from the naturalistic bronzes of Ilé-Ifẹ̀ to vibrant adire textiles, intricate beadwork, and the living arts of music and dance.",
    sections: [
      {
        heading: "Sculpture and Casting",
        body: [
          "The bronze and terracotta heads of Ifẹ̀, created centuries ago, remain among the most celebrated achievements in world art for their lifelike realism. Yorùbá wood carvers produced masks, house posts, divination trays (ọpọ́n Ifá), and shrine figures rich with symbolism.",
        ],
      },
      {
        heading: "Textiles and Beadwork",
        body: [
          "Adire — indigo resist-dyed cloth — is a signature Yorùbá art, its patterns hand-drawn or stencilled with endless invention. Aṣọ-òkè, richly woven strip cloth, is worn for weddings and festivals. Elaborate beadwork adorns crowns, staffs, and the regalia of priests and kings.",
        ],
      },
      {
        heading: "Music, Dance, and Drama",
        body: [
          "Drumming — especially the dùndún talking drum and the bàtá — is central to Yorùbá life, communicating language, praise, and prayer. This heritage shaped global music, feeding directly into jùjú, fújì, Afrobeat, and the rhythms of the Americas.",
        ],
      },
    ],
    related: ["traditional-clothing", "festivals", "language", "global-influence-diaspora"],
  },

  "global-influence-diaspora": {
    slug: "global-influence-diaspora",
    title: "Global Influence & Diaspora",
    category: "learning",
    categoryLabel: "Who Are the Yorùbá?",
    hero: "https://static.prod-images.emergentagent.com/jobs/505cc825-2461-4ba6-922d-8b16e3d69521/images/fcefa15f352ca10f191d755b723db3ea81851211d14117b7dbaf0b2b5dd30350.jpeg",
    metaDescription:
      "How Yorùbá culture spread across the globe — shaping religion, music, and identity in Brazil, Cuba, the Caribbean, and the United States.",
    keywords: ["Yorùbá diaspora", "Candomblé", "Santería", "African diaspora", "Yorùbá influence"],
    intro:
      "Through the transatlantic slave trade and later voluntary migration, Yorùbá culture became a global force. Its deities, rhythms, and philosophy took root across the Americas, where they endure and flourish today.",
    sections: [
      {
        heading: "New World Religions",
        body: [
          "In Brazil, Yorùbá spirituality became Candomblé; in Cuba, Santería (Lucumí); in Trinidad, Òrìṣà worship. The Òrìṣà — Ṣàngó, Ọ̀ṣun, Yemọja, Ògún — crossed the ocean and continue to be venerated by millions, often blended with Catholic imagery.",
        ],
      },
      {
        heading: "Rhythm That Changed the World",
        body: [
          "Yorùbá drumming and call-and-response singing are ancestors of samba, salsa, rumba, jazz, and much of modern popular music. The sacred bàtá rhythms of Cuba trace directly to Yorùbá worship.",
        ],
      },
      {
        heading: "A Reconnecting World",
        body: [
          "Today, people across the diaspora — including growing communities in the United States, such as here in Rhode Island — are reclaiming Yorùbá names, language, and spirituality as a source of pride and identity.",
        ],
      },
    ],
    related: ["modern-relevance", "religion-and-spirituality", "orisa-encyclopedia"],
  },

  "yoruba-identity-today": {
    slug: "yoruba-identity-today",
    title: "Yorùbá Identity Today",
    category: "learning",
    categoryLabel: "Who Are the Yorùbá?",
    hero: IMAGES.geleMulti,
    metaDescription:
      "What it means to be Yorùbá today — a dynamic identity that blends deep tradition with modern life across Africa and the diaspora.",
    keywords: ["Yorùbá identity", "modern Yorùbá", "Yorùbá today", "cultural pride"],
    intro:
      "Yorùbá identity today is dynamic and confident — rooted in ancient tradition yet fully engaged with the modern world. It lives in language, faith, dress, cuisine, art, and a shared sense of belonging that spans continents.",
    sections: [
      {
        heading: "Tradition Meets Modernity",
        body: [
          "Contemporary Yorùbá people navigate multiple worlds with ease — celebrating naming ceremonies and festivals while excelling in science, business, film, and the arts. Nollywood, one of the world's largest film industries, carries Yorùbá stories to global audiences.",
        ],
      },
      {
        heading: "Faith and Plurality",
        body: [
          "Many Yorùbá today are Muslim or Christian, yet traditional values, proverbs, and Ìṣẹ̀ṣe practices remain deeply woven into everyday life. This coexistence is often held up as a model of religious tolerance.",
        ],
      },
      {
        heading: "A Proud, Global People",
        body: [
          "From Lagos to London, São Paulo to New England, Yorùbá communities preserve their heritage through festivals, associations, and cultural education — ensuring the identity remains vibrant for future generations.",
        ],
      },
    ],
    related: ["modern-relevance", "why-yoruba-matter", "traditional-clothing"],
  },

  "why-yoruba-matter": {
    slug: "why-yoruba-matter",
    title: "Why the Yorùbá Matter",
    category: "learning",
    categoryLabel: "Who Are the Yorùbá?",
    hero: IMAGES.womanBeaded,
    metaDescription:
      "Why Yorùbá culture matters to the world — a civilisation whose philosophy, art, and spirituality offer enduring wisdom for humanity.",
    keywords: ["Yorùbá importance", "Yorùbá civilisation", "African philosophy", "cultural heritage"],
    intro:
      "The Yorùbá matter not only because of their numbers, but because of the depth of what they have given the world: a sophisticated philosophy of character, a resilient spirituality, and an artistic legacy that reshaped global culture.",
    sections: [
      {
        heading: "A Philosophy of Character",
        body: [
          "The Yorùbá ideal of Omolúàbí — that a person's worth is measured by their character — offers a timeless ethical framework. Their emphasis on destiny (àyànmọ́), balance, and community speaks to universal human concerns.",
        ],
      },
      {
        heading: "A Living Spiritual Tradition",
        body: [
          "Yorùbá spirituality is one of the few African religions to survive, adapt, and spread globally. It demonstrates the resilience of African thought and continues to guide millions of adherents worldwide.",
        ],
      },
      {
        heading: "A Gift to Global Culture",
        body: [
          "From the bronzes of Ifẹ̀ to the rhythms in modern music, Yorùbá creativity is woven into the fabric of world civilisation. To learn about the Yorùbá is to understand a vital chapter in the story of humanity.",
        ],
      },
    ],
    related: ["omoluabi", "worldview-philosophy", "global-influence-diaspora"],
  },

  // ============ DEEP KNOWLEDGE ============
  mythology: {
    slug: "mythology",
    title: "Yorùbá Mythology — A Deep Dive",
    category: "deep",
    categoryLabel: "Deep Knowledge",
    hero: IMAGES.seatedFigure,
    metaDescription:
      "A deep dive into Yorùbá mythology — the creation of the world at Ilé-Ifẹ̀, Odùduwà and Ọbàtálá, and the epic stories of the Òrìṣà.",
    keywords: ["Yorùbá mythology", "creation myth", "Ọbàtálá", "Odùduwà", "Òrìṣà stories"],
    intro:
      "Yorùbá mythology is a vast and vivid cosmology explaining the origins of the world, the nature of the divine, and the moral order of existence. Its stories are preserved in the sacred verses of Ifá and in generations of oral storytelling.",
    sections: [
      {
        heading: "The Descent from the Sky",
        body: [
          "In the beginning, the Yorùbá say, there was only sky above and water below. Olódùmarè entrusted the creation of solid earth to the Òrìṣà. In one telling, Ọbàtálá was chosen but grew drunk on palm wine; in another, Odùduwà completed the task, descending on a chain with a handful of earth, a five-toed hen, and a palm nut.",
          "Where the earth was scattered, land formed — and that place became Ilé-Ifẹ̀, 'the source of spreading.' From here, creation extended across the world.",
        ],
      },
      {
        heading: "Ọbàtálá, Sculptor of Humanity",
        body: [
          "Ọbàtálá was given the task of moulding human bodies from clay, which Olódùmarè then brought to life. This is why Ọbàtálá is honoured as the creator of humankind and the patron of those born with physical differences, who are considered sacred to him.",
        ],
      },
      {
        heading: "Stories of the Òrìṣà",
        body: [
          "Yorùbá myth is filled with dramatic narratives: the tragic majesty of Ṣàngó, who became the thunder; the beauty and power of Ọ̀ṣun, whose river saved a people; the pact with Èṣù, the divine messenger who guards the crossroads. These stories are not mere tales — they encode ethics, cosmology, and the workings of Àṣẹ.",
        ],
      },
    ],
    related: ["orisa-encyclopedia", "religion-and-spirituality", "worldview-philosophy", "history-timeline"],
  },

  "worldview-philosophy": {
    slug: "worldview-philosophy",
    title: "Yorùbá Worldview & Philosophy",
    category: "deep",
    categoryLabel: "Deep Knowledge",
    hero: IMAGES.manScarification,
    metaDescription:
      "Explore the Yorùbá worldview — Àṣẹ, destiny, the balance of Ayé and Ọ̀run, and a philosophy of harmony between the seen and unseen worlds.",
    keywords: ["Yorùbá worldview", "Yorùbá philosophy", "Àṣẹ", "Ayé and Ọ̀run", "destiny"],
    intro:
      "The Yorùbá worldview is a coherent philosophy of existence in which the visible and invisible worlds are inseparable, all things are charged with divine energy, and each person arrives on earth with a chosen destiny to fulfil.",
    sections: [
      {
        heading: "Àṣẹ: The Power to Make Things Happen",
        body: [
          "Àṣẹ is the vital force that flows through all existence — the power that turns words into reality and intention into event. It resides in speech, in nature, in ritual, and in the authority of elders and kings. To act with Àṣẹ is to act with efficacy and blessing.",
        ],
      },
      {
        heading: "Ayé and Ọ̀run: Two Worlds, One Reality",
        body: [
          "The Yorùbá speak of Ayé (the visible, physical world) and Ọ̀run (the invisible realm of the Òrìṣà, ancestors, and unborn). These are not opposed but continuous — a person moves between them, and the two are in constant conversation. Life is the art of maintaining balance between them.",
        ],
      },
      {
        heading: "Destiny and Character",
        body: [
          "Before birth, each person kneels before Olódùmarè and chooses their destiny (àyànmọ́), sealed in the inner head, Orí. Yet destiny is not fixed fate: good character (ìwà) and right action can help one fulfil a good destiny, while poor character can undermine it. Philosophy here is deeply practical — a guide to living well.",
        ],
      },
    ],
    related: ["ori", "omoluabi", "ifa-philosophy", "concept-of-soul"],
  },

  festivals: {
    slug: "festivals",
    title: "Yorùbá Festivals",
    category: "deep",
    categoryLabel: "Deep Knowledge",
    hero: "https://static.prod-images.emergentagent.com/jobs/505cc825-2461-4ba6-922d-8b16e3d69521/images/2d3bccc07d7d5e3fe91245028dd52f130624a1e15c7df8834c159c5557f2160e.jpeg",
    metaDescription:
      "Discover the great Yorùbá festivals — Ọ̀ṣun-Òṣogbo, Egúngún, Odún Ifá, and more — vibrant celebrations of the Òrìṣà, ancestors, and community.",
    keywords: ["Yorùbá festivals", "Osun Osogbo", "Egungun festival", "African festivals"],
    intro:
      "Festivals (ọdún) are the heartbeat of Yorùbá communal life — spectacular annual celebrations that honour the Òrìṣà and ancestors, renew social bonds, and pass tradition to the next generation through music, masquerade, and ritual.",
    sections: [
      {
        heading: "Ọ̀ṣun-Òṣogbo Festival",
        body: [
          "Held each August along the Ọ̀ṣun River, this two-week festival honours the goddess Ọ̀ṣun and draws devotees and visitors from around the world. The sacred grove of Òṣogbo, where it culminates, is a UNESCO World Heritage Site — a living sanctuary of Yorùbá spirituality and art.",
        ],
      },
      {
        heading: "Egúngún Masquerades",
        body: [
          "The Egúngún festival celebrates the ancestors, who return to the community embodied in spectacular masqueraders draped in layered, richly patterned cloth. Through dance and blessing, the living and the dead are reunited, and the community's continuity is affirmed.",
        ],
      },
      {
        heading: "Odún Ifá and Local Celebrations",
        body: [
          "The annual Ifá festival renews the community's relationship with Ọ̀rúnmìlà and destiny. Across Yorùbáland, countless local festivals honour town deities, mark harvests, and celebrate kingship — each with its own music, foods, and sacred rites.",
        ],
      },
    ],
    related: ["orisa-encyclopedia", "traditional-clothing", "death-and-ancestors", "arts-culture-creativity"],
  },

  "traditional-clothing": {
    slug: "traditional-clothing",
    title: "Traditional Clothing",
    category: "deep",
    categoryLabel: "Deep Knowledge",
    hero: IMAGES.geleBlue,
    metaDescription:
      "Explore Yorùbá traditional clothing — aṣọ-òkè, adire, the elegant gèlè head wrap, agbádá, and the meaning woven into every garment.",
    keywords: ["Yorùbá clothing", "aso oke", "gele", "adire", "agbada", "Yorùbá fashion"],
    intro:
      "Yorùbá dress is a language of its own — an art of colour, cloth, and craftsmanship that signals status, occasion, and identity. From everyday wear to the grandeur of ceremonial attire, clothing is central to Yorùbá aesthetics.",
    sections: [
      {
        heading: "Aṣọ-Òkè and Adire",
        body: [
          "Aṣọ-òkè is a prestigious hand-woven strip cloth, often shot with metallic threads, worn for weddings, chieftaincy, and festivals. Adire, the indigo resist-dyed cloth, showcases the Yorùbá genius for pattern and remains a beloved everyday and fashion fabric.",
        ],
      },
      {
        heading: "The Art of the Gèlè",
        body: [
          "For women, the gèlè — an elaborately tied head wrap — is a crowning statement of elegance, its dramatic folds a mark of skill and celebration. Paired with the ìró (wrapper), bùbá (blouse), and ìpèlé (shoulder sash), it completes a look of refined beauty.",
        ],
      },
      {
        heading: "Men's Regalia",
        body: [
          "Men wear the flowing agbádá (a wide-sleeved robe), the fitted bùbá and ṣòkòtò (trousers), and caps such as the fìlà abetí-ajá. Coral beads and specific fabrics signal chieftaincy and royal rank, tying dress directly to the social order.",
        ],
      },
    ],
    related: ["arts-culture-creativity", "kingship", "festivals", "yoruba-identity-today"],
  },

  "naming-system": {
    slug: "naming-system",
    title: "The Yorùbá Naming System",
    category: "deep",
    categoryLabel: "Deep Knowledge",
    hero: IMAGES.geleMulti,
    metaDescription:
      "Understand the profound Yorùbá naming system — the ìsọmọlórúkọ ceremony, and how names carry destiny, circumstance, and family history.",
    keywords: ["Yorùbá names", "naming ceremony", "ìsọmọlórúkọ", "Yorùbá naming system"],
    intro:
      "Among the Yorùbá, a name is never arbitrary. The proverb 'Ilé ni à ń wò kí á tó sọ ọmọ lórúkọ' — we consider the family before naming a child — captures a belief that names carry meaning, prophecy, and identity.",
    sections: [
      {
        heading: "The Naming Ceremony (Ìsọmọlórúkọ)",
        body: [
          "A child is formally named on the seventh, eighth, or ninth day after birth in a joyful ceremony. Symbolic items are tasted — honey for sweetness, salt for flavour and wisdom, kola nut, water, and more — each carrying prayers for the child's life.",
        ],
      },
      {
        heading: "Types of Names",
        body: [
          "Yorùbá names fall into several kinds: Orúkọ àmútọ̀runwá (names tied to circumstances of birth, such as Táíwò and Kẹ́hìndé for twins), Orúkọ àbísọ (given names chosen by the family), and Oríkì (praise names). Names often reference God (Olú-, -ọlá), deities, or family fortunes.",
        ],
      },
      {
        heading: "Names as Destiny",
        body: [
          "A name is a lifelong prayer and a compass. The Yorùbá believe a person tends to live out the meaning of their name, so families choose with great care — encoding hopes, gratitude, and the family's story into a single, sacred word.",
        ],
      },
    ],
    related: ["social-structure", "life-stages", "language", "ori"],
  },

  kingship: {
    slug: "kingship",
    title: "Kingship & Political Structure",
    category: "deep",
    categoryLabel: "Deep Knowledge",
    hero: IMAGES.beadedCrownWoman,
    metaDescription:
      "Explore Yorùbá kingship and politics — the sacred ọba, the beaded crown (adé), councils of chiefs, and a system of checks and balances.",
    keywords: ["Yorùbá kingship", "oba", "beaded crown", "Yorùbá politics", "Aláàfin"],
    intro:
      "Yorùbá political tradition centres on sacred kingship. The ọba (king) is far more than a ruler — he is a living link to the ancestors and to Odùduwà himself, his authority balanced by powerful councils in a sophisticated system of governance.",
    sections: [
      {
        heading: "The Sacred Ọba and the Adé",
        body: [
          "The ọba wears the beaded crown (adé), whose veil of beads hides his face and transforms him into a vessel of ancestral power. Titles such as the Ọọ̀ni of Ifẹ̀ and the Aláàfin of Ọ̀yọ́ carry immense spiritual and cultural weight to this day.",
        ],
      },
      {
        heading: "Checks and Balances",
        body: [
          "Yorùbá kingship was never absolute. In Ọ̀yọ́, the Ọ̀yọ́ Mèsì council of chiefs, led by the Baṣọ̀run, could check the Aláàfin's power — even compelling a tyrannical king to abdicate. Councils, guilds, and priesthoods distributed authority across society.",
        ],
      },
      {
        heading: "Kingship Today",
        body: [
          "Though modern nation-states now govern, Yorùbá ọba remain revered custodians of culture and tradition, presiding over festivals, resolving disputes, and embodying the continuity of their people across the centuries.",
        ],
      },
    ],
    related: ["origins-and-history", "history-timeline", "traditional-clothing", "festivals"],
  },

  // ============ PHILOSOPHY & SPIRITUALITY ============
  omoluabi: {
    slug: "omoluabi",
    title: "Omolúàbí Philosophy — A Deep Dive",
    category: "philosophy",
    categoryLabel: "Philosophy & Spirituality",
    hero: IMAGES.elderMan,
    metaDescription:
      "A deep dive into Omolúàbí — the Yorùbá philosophy of good character (ìwà rere) that defines the ideal human being.",
    keywords: ["Omolúàbí", "Yorùbá philosophy", "ìwà", "good character", "Yorùbá ethics"],
    intro:
      "Omolúàbí is the crowning ideal of Yorùbá philosophy — the fully realised human being of good character. The word itself, 'ọmọ tí olú-ìwà bí,' means 'the child born of the chief of character.' To be called an Omolúàbí is the highest praise in Yorùbá society.",
    sections: [
      {
        heading: "Character Above All: Ìwà",
        body: [
          "The Yorùbá say 'Ìwà l'ẹwà' — character is beauty. A person may possess wealth, intelligence, or beauty, but without good character (ìwà rere) they are incomplete. Character is the true and lasting measure of a human being.",
        ],
      },
      {
        heading: "The Virtues of an Omolúàbí",
        body: [
          "An Omolúàbí embodies a cluster of virtues that together form a whole person of integrity.",
        ],
        list: [
          "Ọ̀rọ̀ sísọ — speaking with wisdom and truth",
          "Ìtẹríba — respect and humility, especially toward elders",
          "Inú rere — good-heartedness and generosity",
          "Òtítọ́ — honesty and truthfulness",
          "Ìwà pẹ̀lẹ́ — gentleness and good conduct",
          "Akíkanjú — courage and diligence in doing good",
        ],
      },
      {
        heading: "A Living Ethical Compass",
        body: [
          "Omolúàbí is not an abstract theory but a daily practice — a standard by which children are raised and adults are judged. It anchors Yorùbá ethics in the conviction that how we treat others, and the quality of our character, is what truly matters.",
        ],
      },
    ],
    related: ["ethics-moral-system", "worldview-philosophy", "ori", "social-structure"],
  },

  ori: {
    slug: "ori",
    title: "Orí Explained — A Beginner's Guide",
    category: "philosophy",
    categoryLabel: "Philosophy & Spirituality",
    hero: IMAGES.womanKenteCrown,
    metaDescription:
      "A beginner's guide to Orí — the Yorùbá concept of the inner head, the seat of destiny and the most personal of all divine forces.",
    keywords: ["Orí", "Yorùbá destiny", "inner head", "àyànmọ́", "Yorùbá spirituality"],
    intro:
      "Orí is one of the most beautiful and personal ideas in Yorùbá thought. Literally meaning 'head,' Orí refers to the inner spiritual head — the bearer of one's destiny and, in a sense, one's own personal divinity.",
    sections: [
      {
        heading: "The Inner Head",
        body: [
          "The Yorùbá distinguish the physical head (orí òde) from the inner, spiritual head (orí inú). It is the orí inú that carries a person's destiny and guides them through life. Many say your Orí is the Òrìṣà closest to you — the one you must honour first.",
        ],
      },
      {
        heading: "Choosing Your Destiny",
        body: [
          "Before birth, tradition says, each soul kneels in heaven and chooses an Orí — and with it, the outline of its destiny (àyànmọ́). Because the choice is made before entering the world, the details are forgotten, and life becomes the unfolding of that hidden design.",
        ],
      },
      {
        heading: "Honouring the Orí",
        body: [
          "Because Orí governs one's fortunes, the Yorùbá honour it through prayer and ritual (ìbọrí). To have a 'good Orí' (orí rere) is to be blessed with a favourable destiny — but good character and wise action are needed to help that destiny come to pass.",
        ],
      },
    ],
    related: ["worldview-philosophy", "concept-of-soul", "ifa-philosophy", "omoluabi"],
  },

  "ifa-philosophy": {
    slug: "ifa-philosophy",
    title: "Ifá Philosophy Simplified",
    category: "philosophy",
    categoryLabel: "Philosophy & Spirituality",
    hero: IMAGES.woodCarving,
    metaDescription:
      "Ifá philosophy simplified — an accessible guide to the Yorùbá system of wisdom, divination, and destiny associated with Ọ̀rúnmìlà.",
    keywords: ["Ifá philosophy", "Ifá divination", "Ọ̀rúnmìlà", "odù Ifá", "babaláwo"],
    intro:
      "Ifá is the profound Yorùbá system of wisdom and divination — a vast literary and philosophical corpus that guides individuals and communities toward harmony with their destiny. In 2005, UNESCO honoured it as a masterpiece of humanity's intangible heritage.",
    sections: [
      {
        heading: "Ọ̀rúnmìlà, the Witness to Destiny",
        body: [
          "Ifá is associated with the Òrìṣà Ọ̀rúnmìlà, the deity of wisdom who, according to tradition, was present when each person chose their destiny. Because he 'witnessed' this choice, Ọ̀rúnmìlà can help people understand and align with their path.",
        ],
      },
      {
        heading: "The Odù and the Babaláwo",
        body: [
          "The Ifá corpus is organised into 256 odù — chapters, each containing countless verses (ẹsẹ Ifá) filled with stories, proverbs, and guidance. A trained priest, the babaláwo ('father of secrets'), uses sacred palm nuts or a divining chain (ọ̀pẹ̀lẹ̀) to identify the odù relevant to a situation, then interprets its wisdom.",
        ],
      },
      {
        heading: "A Philosophy of Balance",
        body: [
          "At its heart, Ifá is less about predicting the future and more about guidance — helping people restore balance, make wise choices, and live in harmony with themselves, their community, and the cosmos. It is a living library of Yorùbá thought.",
        ],
      },
    ],
    related: ["orisa-encyclopedia", "worldview-philosophy", "ori", "religion-and-spirituality"],
  },

  "concept-of-soul": {
    slug: "concept-of-soul",
    title: "The Yorùbá Concept of the Soul",
    category: "philosophy",
    categoryLabel: "Philosophy & Spirituality",
    hero: IMAGES.maternityStatue,
    metaDescription:
      "Explore the Yorùbá concept of the soul — Èmí, Orí, and Ẹ̀mí, and the multi-layered vision of the human person in Yorùbá philosophy.",
    keywords: ["Yorùbá soul", "Èmí", "Orí", "Yorùbá metaphysics", "human person"],
    intro:
      "Yorùbá philosophy holds a rich, layered vision of the human person. Rather than a single soul, it describes several spiritual components that together make a living, conscious being — an idea of remarkable subtlety.",
    sections: [
      {
        heading: "Èmí — The Breath of Life",
        body: [
          "Èmí is the life-force or breath, associated with Olódùmarè, that animates the body. When Èmí departs, physical life ends. It is the divine spark that makes a body alive rather than mere clay.",
        ],
      },
      {
        heading: "Orí — The Inner Self and Destiny",
        body: [
          "Orí, the inner head, is the seat of individuality and destiny. It is the most personal spiritual element — the part of a person that carries their unique path and, some say, survives to be reborn.",
        ],
      },
      {
        heading: "Ọkàn and the Whole Person",
        body: [
          "Ọkàn (the heart) is linked to thought, emotion, and conscience. Together, Èmí, Orí, Ọkàn, and the body (ara) form the complete human being. This layered anthropology reflects the Yorùbá conviction that the person is a meeting point of the physical and the divine.",
        ],
      },
    ],
    related: ["ori", "worldview-philosophy", "death-and-ancestors", "life-stages"],
  },

  "ethics-moral-system": {
    slug: "ethics-moral-system",
    title: "Yorùbá Ethics & Moral System",
    category: "philosophy",
    categoryLabel: "Philosophy & Spirituality",
    hero: IMAGES.traditionalGroup,
    metaDescription:
      "Understand the Yorùbá ethical and moral system — grounded in character, community, respect, and the pursuit of a good and balanced life.",
    keywords: ["Yorùbá ethics", "Yorùbá morality", "ìwà rere", "African ethics", "Omolúàbí"],
    intro:
      "Yorùbá ethics is a practical, community-centred moral system rooted in the cultivation of good character. It asks not merely 'what is right?' but 'what kind of person should I become?' — placing virtue at the heart of the good life.",
    sections: [
      {
        heading: "Character as the Foundation",
        body: [
          "Morality in Yorùbá thought flows from ìwà (character). Right action is the natural expression of a well-formed person. The goal is to become an Omolúàbí — someone whose good character benefits everyone around them.",
        ],
      },
      {
        heading: "Community and Responsibility",
        body: [
          "Ethics is inseparable from community. Actions are judged by how they affect the wider web of family, neighbours, and ancestors. Selfishness, dishonesty, and disrespect are condemned not only as personal failings but as harms to the social fabric.",
        ],
      },
      {
        heading: "Consequence and Balance",
        body: [
          "The Yorùbá recognise moral consequence — 'Ohun tí a bá gbìn ni à ń kà,' we reap what we sow. Ethical life is about maintaining balance and harmony (between people, and between the human and spiritual worlds), guided by wisdom, proverbs, and the teachings of Ifá.",
        ],
      },
    ],
    related: ["omoluabi", "worldview-philosophy", "social-structure", "death-and-ancestors"],
  },

  // ============ LIFE & EXISTENCE ============
  "life-stages": {
    slug: "life-stages",
    title: "Life Stages in Yorùbá Thought",
    category: "life",
    categoryLabel: "Life & Existence",
    hero: IMAGES.maternityStatue,
    metaDescription:
      "Explore the stages of life in Yorùbá thought — birth, naming, adulthood, marriage, elderhood, and the journey of the soul through existence.",
    keywords: ["Yorùbá life stages", "rites of passage", "Yorùbá birth", "elderhood", "Yorùbá culture"],
    intro:
      "For the Yorùbá, life is a sacred journey marked by clear stages and rites of passage. Each transition is honoured with ceremony, prayer, and community — from the moment a soul chooses its destiny to the day it joins the ancestors.",
    sections: [
      {
        heading: "Birth and Naming",
        body: [
          "Life on earth begins as the fulfilment of a destiny chosen in heaven. Birth is celebrated, and within days the child is welcomed into the community through the naming ceremony, which binds them to family and lineage.",
        ],
      },
      {
        heading: "Youth, Adulthood, and Marriage",
        body: [
          "Childhood is a time of learning character and craft. Marriage marks entry into full adulthood and social responsibility — a union not only of two people but of two families, celebrated with rich ceremony. Parenthood deepens one's standing in the community.",
        ],
      },
      {
        heading: "Elderhood and Wisdom",
        body: [
          "Old age is deeply honoured. Elders are living libraries of proverbs, history, and guidance, and their blessings carry great Àṣẹ. To reach elderhood with good character is to become a bridge between the community and the ancestors — the culmination of a life well lived.",
        ],
      },
    ],
    related: ["naming-system", "death-and-ancestors", "social-structure", "concept-of-soul"],
  },

  "death-and-ancestors": {
    slug: "death-and-ancestors",
    title: "Death & Ancestors",
    category: "life",
    categoryLabel: "Life & Existence",
    hero: IMAGES.seatedFigure,
    metaDescription:
      "A respectful exploration of death and ancestors in Yorùbá belief — the transition to Ọ̀run, egúngún, and the enduring bond between the living and the dead.",
    keywords: ["Yorùbá death", "ancestors", "egúngún", "Ọ̀run", "Yorùbá afterlife"],
    intro:
      "In Yorùbá thought, death is not an ending but a transition — a passage from the visible world (Ayé) to the realm of the ancestors (Ọ̀run). The bond between the living and the dead remains unbroken, treated always with reverence and care.",
    sections: [
      {
        heading: "The Journey to Ọ̀run",
        body: [
          "When a person of good character reaches old age and dies, it is understood as a homecoming — a return to Ọ̀run and to the ancestors. Elaborate funeral rites help ensure a peaceful transition and honour the life that was lived.",
        ],
      },
      {
        heading: "The Ancestors (Egúngún)",
        body: [
          "The ancestors remain active in the life of the family and community. They are honoured, consulted, and remembered — not as distant memories but as present guardians. During the Egúngún festival, they return in masquerade to bless and guide the living.",
        ],
      },
      {
        heading: "Continuity and Reincarnation",
        body: [
          "The Yorùbá believe deeply in continuity. Names such as Babatúndé ('father has returned') and Yétúndé ('mother has returned') reflect the belief that ancestors may return through new births. Life, death, and rebirth form one unbroken circle — a source of comfort and profound respect.",
        ],
      },
    ],
    related: ["life-stages", "concept-of-soul", "festivals", "religion-and-spirituality"],
  },

  // ============ MODERN RELEVANCE ============
  "modern-relevance": {
    slug: "modern-relevance",
    title: "The Modern Relevance of Yorùbá Culture",
    category: "modern",
    categoryLabel: "Modern Relevance",
    hero: IMAGES.festivalCrowd,
    metaDescription:
      "How Yorùbá culture shapes the modern world — global spirituality, art, music, fashion, and thriving diaspora communities from Lagos to Rhode Island.",
    keywords: ["Yorùbá culture today", "African spirituality", "Yorùbá influence", "diaspora", "Afrobeats", "Òrìṣà"],
    intro:
      "Far from being a relic of the past, Yorùbá culture is a living, global force shaping spirituality, art, music, and identity in the twenty-first century. Its influence radiates from Lagos across the Atlantic and into communities worldwide.",
    sections: [
      {
        heading: "Global Spirituality",
        body: [
          "Yorùbá-derived religions — Candomblé, Santería, and Òrìṣà traditions — are practised by millions across the Americas and are growing among people reconnecting with African spirituality. The wisdom of Ifá and the veneration of the Òrìṣà continue to guide modern seekers around the world.",
        ],
      },
      {
        heading: "Art, Music, and Fashion",
        body: [
          "The global rise of Afrobeats — from artists like Fela Kuti's legacy to today's chart-topping stars — carries Yorùbá rhythm and language to billions of listeners. Yorùbá aesthetics inspire contemporary fashion, from adire prints on international runways to the gèlè at global celebrations, while Nollywood exports Yorùbá stories worldwide.",
        ],
      },
      {
        heading: "Diaspora Communities",
        body: [
          "Yorùbá diaspora communities — including those in Rhode Island and across the United States — keep heritage alive through festivals, language classes, naming ceremonies, and cultural organisations. This is the very mission of ÀsÀNigHT Worldwide: to preserve, educate, and inspire cultural pride for a global audience.",
        ],
      },
    ],
    related: ["global-influence-diaspora", "yoruba-identity-today", "arts-culture-creativity", "religion-and-spirituality"],
  },
};

export const getTopic = (slug) => TOPICS[slug];
export const allTopics = () => Object.values(TOPICS);
